import { createPedido, getPedido, updatePedido } from "@spark/services/pedidos";
import { getOperacoes } from "@spark/services/operacoes";
import { getClientes, getCliente, getModelos, getTiposCarga, getRotas } from "@spark/services";
import React, { useMemo, useCallback, useState, useEffect } from 'react';
import { Form } from 'react-final-form';
import { useHistory, useParams } from 'react-router-dom';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import { Card, CardBody } from '@spark/ui-components/components/Card';
import { FormButtonToolbar, FormContainer } from '@spark/ui-components/components/form/FormElements';
import { formStructure, formSections, validateForm, getFormStructure, getFormSections } from './formConfig';
import DynamicForm from '@spark/ui-components/components/form/DynamicForm';
import ClienteSelectionModal from '../../../../Clientes/components/ClienteSelectionModal';
import RotaSelectionModal from './components/RotaSelectionModal';
import RotaMapModal from './components/RotaMapModal';
import { notification } from '@spark/ui-components/utils/notification';

const tipoTomadorOptions = [
  { value: '0', label: 'Remetente' },
  { value: '1', label: 'Expedidor' },
  { value: '2', label: 'Recebedor' },
  { value: '3', label: 'Destinatário' },
  { value: '4', label: 'Outros' }
];
const requisitanteOptions = [
  { value: '0', label: 'Remetente' },
  { value: '1', label: 'Destinatário' },
  { value: '2', label: 'Outros' },
  { value: '3', label: 'Redespacho' },
  { value: '4', label: 'Terceiro' },
  { value: '5', label: 'Recebedor' }
];
const getOptionLabel = (options, value) => {
  if (value === null || value === undefined || value === '') return '';
  const found = options.find((option) => String(option.value) === String(value));
  return found ? found.label : String(value);
};

// helper de data -> "YYYY-MM-DD HH:mm:ss"
const toApiDateTime = (value) => {
  if (!value) return null;

  // DynamicForm pode devolver Date, string ISO, ou string de input
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return null;

  const pad = (n) => String(n).padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  const ss = pad(d.getSeconds());
  return `${yyyy}-${mm}-${dd} ${hh}:${mi}:${ss}`;
};

const toApiDateTimeBr = (value) => {
  if (!value) return null;
  if (typeof value === 'string' && value.includes('/')) return value;
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const parseBrDateTime = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;

  if (typeof value === 'string') {
    if (value.includes('/')) {
      const [datePart, timePart = '00:00:00'] = value.split(' ');
      const [dd, mm, yyyy] = datePart.split('/');
      const [hh = '00', mi = '00', ss = '00'] = timePart.split(':');
      const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd), Number(hh), Number(mi), Number(ss));
      return isNaN(d.getTime()) ? null : d;
    }

    if (value.includes('T')) {
      // ISO sem timezone deve ser tratado como horário local (sem shift)
      const hasTz = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value);
      if (!hasTz) {
        const [datePart, timePart = '00:00:00'] = value.split('T');
        const [yyyy, mm, dd] = datePart.split('-');
        const [hh = '00', mi = '00', ss = '00'] = timePart.replace('Z', '').split(':');
        const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd), Number(hh), Number(mi), Number(ss));
        return isNaN(d.getTime()) ? null : d;
      }
    }
  }

  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

const extractDigits = (value) => {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits.length > 0 ? digits : null;
};

const generateNumeroPedido = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const dd = pad(d.getDate());
  const mm = pad(d.getMonth() + 1);
  const yyyy = d.getFullYear();
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  const ss = pad(d.getSeconds());
  return `${dd}${mm}${yyyy}${hh}${mi}${ss}`;
};

const mapFormToCreateApi = (values, depotId, { formatDataColetaBr = false } = {}) => {
  const num = (v) => {
    if (v === '' || v === null || v === undefined) return null;
    const s = typeof v === 'string' ? v.replace(',', '.') : v;
    const n = parseFloat(s);
    return isNaN(n) ? null : n;
  };
  const intOrNull = (v) => {
    if (v === '' || v === null || v === undefined) return null;
    const n = parseInt(v, 10);
    return isNaN(n) ? null : n;
    };
  const extractSelectId = (value) => {
    if (!value) return null;
    if (typeof value === 'object') {
      return value.value ?? value.Id ?? value.id ?? null;
    }
    return value;
  };
  const selectValue = (value) => {
    if (!value) return null;
    if (typeof value === 'object') {
      return (
        value.value ??
        value.CodigoIntegracao ??
        value.codigoIntegracao ??
        value.Id ??
        value.id ??
        null
      );
    }
    return value;
  };
  const extractLabelValue = (value) => {
    if (!value) return null;
    if (typeof value === 'object') {
      return value.label ?? value.value ?? null;
    }
    return value;
  };
  const extractTomador = (value) => {
    if (!value) return null;
    if (typeof value === 'object') {
      return (
        value.cnpj ??
        value.Cnpj ??
        value.CNPJ ??
        value.label ??
        value.value ??
        value.Nome ??
        value.nome ??
        null
      );
    }
    return value;
  };

  const tipoDeCarga =
    values.tipoDeCarga?.value ||
    values.tipoDeCarga?.CodigoIntegracao ||
    values.tipoDeCarga;

  const tipoDeOperacao =
    values.tipoDeOperacao?.value ||
    values.tipoDeOperacao?.TipoDaOperacao ||
    values.tipoDeOperacao;
  const modeloVeicularSolicitado =
    values.modeloVeicularSolicitado?.label ||
    values.modeloVeicularSolicitado?.Placa ||
    values.modeloVeicularSolicitado;
  const modeloVeicularSolicitadoId =
    values.modeloVeicularSolicitado?.value ||
    values.modeloVeicularSolicitado?.Id ||
    values.modeloVeicularSolicitadoId;

  return {
    // originais
    IdCarga: null,
    DepotId: depotId ? parseInt(depotId, 10) : null,
    NumeroDoPedido: values.numeroDoPedido,
    LocalPalletizacao: values.localPalletizacao || null, 
    DataAgendamento: toApiDateTime(values.dataAgendamento) || null,
    DataIntegracao: toApiDateTime(new Date()) || null,

    // novas colunas
    NumeroDePallets: num(values.numeroDePallets) || null,
    PesoLiquido: num(values.pesoLiquido) || null,
    TipoDeRemetente: values.tipoDeRemetente || null,
    NumeroPedidoEmbarcador: values.numeroPedidoEmbarcador || null,
    DUIMP: values.duimp || null,
    DataColeta: (formatDataColetaBr ? toApiDateTimeBr(values.dataColeta) : toApiDateTime(values.dataColeta)) || null,
    Remetente: extractLabelValue(values.remetente),
    Destinatario: extractLabelValue(values.destinatario),
    Recebedor: extractLabelValue(values.recebedor),
    Origem: values.origem || null,
    Destino: values.destino || null,
    TipoDeCarga: tipoDeCarga || null,
    TipoDeOperacao: tipoDeOperacao || null,
    EmpresaFilial: values.empresaFilial || null,
    ModeloVeicularSolicitado: modeloVeicularSolicitado || null,
    ModeloVeicularSolicitadoId: intOrNull(modeloVeicularSolicitadoId) || null,
    DataPrevisaoSaida: toApiDateTime(values.dataPrevisaoSaida) || null,
    DataPrevisaoEntregaRemetente: toApiDateTime(values.dataPrevisaoEntregaRemetente) || null,
    DataInicialViagemExecutada: toApiDateTime(values.dataInicialViagemExecutada) || null,
    DataFinalViagemExecutada: toApiDateTime(values.dataFinalViagemExecutada) || null,
    DataInicialViagemFaturada: toApiDateTime(values.dataInicialViagemFaturada) || null,
    DataFinalViagemFaturada: toApiDateTime(values.dataFinalViagemFaturada) || null,
    NomeTipoTomador:
      values.tomador && typeof values.tomador === 'object'
        ? values.tomador.label ?? values.tomador.value ?? null
        : values.tomador ?? null,
    CodigoTipoRequisitante: values.requisitante || null,
    NomeTipoRequisitante:
      values.requisitante && typeof values.requisitante === 'object'
        ? values.requisitante.label ?? values.requisitante.value ?? null
        : null,
    PesoDoPedido: num(values.pesoDoPedido),
    PesoLiquido: num(values.pesoDoPedido), // Adicionar 
    QuantidadeDeEntrega: intOrNull(values.quantidadeDeEntrega) || null,
    DataAgendamentoPedido: toApiDateTime(values.dataAgendamentoPedido) || null,
    CondicaoDoPedido: values.condicaoDoPedido || null,
    ObservacaoPedido: values.observacaoPedido || null,
    Frota: values.frota || null,
    CubagemPedido: values.CubagemPedido || null,
    TransportadoraEmitenteId: values.transportadoraEmitenteId || null,
    RemetenteId: intOrNull(extractSelectId(values.remetente)) || values.RemetenteId || null,
    DestinatarioId: intOrNull(extractSelectId(values.destinatario)) || values.DestinatarioId || null,
    RecebedorId: intOrNull(extractSelectId(values.recebedor)) || values.RecebedorId || null,
    TomadorId: intOrNull(extractSelectId(values.tomador)) || values.TomadorId || null,
    CodigoTipoTomador: values.tipoTomador ?? values.TipoTomador ?? null,
    UtilizarTipoTomadorInformado: true,
    TipoPagamento: values.TipoPagamento || null,
    TipoOperacaoCodigoId: 0,
    TipoCargaEmbarcadorCodigo: values.TipoCargaEmbarcadorCodigo || null,
    ValorFrete: num(values.valorFrete),
    ValorTotalMercadoria: num(values.valorTotalMercadoria),
    StatusPedidoEmissaoTMS: null,
    ProtocoloIntegracaoPedido: null,
    PesoBruto: values.PesoBruto || null,
    ValorTotalPedido: 0,
    RotaCodigo: selectValue(values.rota) || null,
    ...(depotId ? { DepotId: parseInt(depotId, 10) } : {})
  };
};

const Forms = ({ isReadOnly = false, depotId = null, hideObservacaoComplemento = false }) => {
  const history = useHistory();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [initialData, setInitialData] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [showRotaMapModal, setShowRotaMapModal] = useState(false);
  const [rotaMapData, setRotaMapData] = useState(null);

  const isEditMode = !!id && !isReadOnly;
  const isDepot = !!depotId;
  const shouldHideObservacaoComplemento = isReadOnly && hideObservacaoComplemento;

  const fetchRotaOption = useCallback(async (codigo) => {
    if (!codigo) return null;
    try {
      const response = await getRotas({ CodigoIntegracao: codigo, pagina: 1, quantidadePorPagina: 1 });
      const registros = Array.isArray(response)
        ? response
        : response?.registros || response?.data || [];
      const rota = registros?.[0];
      if (!rota) return null;
      const codigoIntegracao = rota.CodigoIntegracao ?? rota.codigoIntegracao ?? codigo;
      const descricao = rota.Descricao ?? rota.descricao ?? String(codigoIntegracao || codigo);
      const label = descricao && codigoIntegracao && String(descricao) !== String(codigoIntegracao)
        ? `${descricao} (${codigoIntegracao})`
        : descricao || String(codigoIntegracao || codigo);
      return { value: codigoIntegracao, label };
    } catch (error) {
      console.error('Erro ao carregar rota:', error);
      return null;
    }
  }, []);

  const handleOpenRotaMap = useCallback(async (fieldValue) => {
    let codigo =
      (typeof fieldValue === 'object' && (fieldValue.value || fieldValue.CodigoIntegracao || fieldValue.codigoIntegracao)) ||
      fieldValue ||
      null;
    if (typeof codigo === 'object') {
      codigo = codigo.value ?? codigo.CodigoIntegracao ?? codigo.codigoIntegracao ?? null;
    }

    const rotaData =
      (typeof fieldValue === 'object' && (fieldValue.data || fieldValue.rota || fieldValue.Rota)) ||
      null;

    setRotaMapData(rotaData);
    setShowRotaMapModal(true);

    if (rotaData?.Caminho || rotaData?.caminho) {
      return;
    }
    notification.error('Rota sem caminho disponivel para exibir no mapa.');
  }, []);

  // carregar para visualização/edição
  useEffect(() => {
    if ((isReadOnly || isEditMode) && id) {
      const loadData = async () => {
        try {
          setLoadingData(true);
      const data = await getPedido(id);
      const rotaCodigo = data?.RotaCodigo ?? data?.Rota ?? data?.rota?.CodigoIntegracao ?? null;
      let rotaOption = null;
      if (data?.rota) {
        const rotaDescricao = data?.rota?.Descricao ?? data?.rota?.descricao ?? '';
        const rotaCodigoObj = data?.rota?.CodigoIntegracao ?? data?.rota?.codigoIntegracao ?? rotaCodigo;
        const rotaLabel = rotaDescricao && rotaCodigoObj && String(rotaDescricao) !== String(rotaCodigoObj)
          ? `${rotaDescricao} (${rotaCodigoObj})`
          : rotaDescricao || String(rotaCodigoObj || '');
        rotaOption = rotaCodigoObj ? { value: rotaCodigoObj, label: rotaLabel, data: data.rota } : null;
      } else if (rotaCodigo) {
        rotaOption = await fetchRotaOption(rotaCodigo);
      }

      let nomeOperacao = data?.operacao?.NomeOperacao || data?.operacao?.nomeOperacao || data?.NomeOperacao || '';
      let tipoDeCargaOption = null;
      let tomadorOption = null;

      const mapClienteOption = (cliente, fallbackId = null, fallbackLabel = '') => {
        if (!cliente && !fallbackId && !fallbackLabel) return null;
        const id = cliente?.Id ?? cliente?.id ?? fallbackId ?? null;
        const nome = cliente?.Nome ?? cliente?.nome ?? '';
        const cnpjRaw = cliente?.Cnpj ?? cliente?.CNPJ ?? cliente?.cnpj ?? '';
        const cnpjDigits = extractDigits(cnpjRaw);
        const label = nome && cnpjRaw ? `${nome} - ${cnpjRaw}` : nome || fallbackLabel || String(id || '');
        return { value: id ?? fallbackId ?? null, label, cnpj: cnpjDigits };
      };

      const remetenteOption = mapClienteOption(data?.remetenteCliente, data?.RemetenteId ?? null, data?.Remetente || '');
      const destinatarioOption = mapClienteOption(data?.destinatarioCliente, data?.DestinatarioId ?? null, data?.Destinatario || '');
      const recebedorOption = mapClienteOption(data?.recebedorCliente, data?.RecebedorId ?? null, data?.Recebedor || '');
      tomadorOption = mapClienteOption(data?.tomadorCliente, data?.TomadorId ?? null, data?.Tomador || '');

      if (data?.tipoCarga) {
        const value =
          data?.tipoCarga?.CodigoIntegracao ??
          data?.tipoCarga?.codigoIntegracao ??
          data?.tipoCarga?.id ??
          data?.tipoCarga?.Id ??
          data?.TipoDeCarga;
        const label = data?.tipoCarga?.Descricao ?? data?.tipoCarga?.descricao ?? String(value || data?.TipoDeCarga || '');
        tipoDeCargaOption = value ? { value, label } : null;
      }

      if (isDepot && !nomeOperacao && data?.TipoDeOperacao) {
        try {
          const operacoesResponse = await getOperacoes({ TipoDaOperacao: data.TipoDeOperacao });
          const registros = Array.isArray(operacoesResponse)
            ? operacoesResponse
            : operacoesResponse?.registros || operacoesResponse?.data || [];
          nomeOperacao = registros?.[0]?.NomeOperacao || registros?.[0]?.nomeOperacao || nomeOperacao;
        } catch (error) {
          console.error('Erro ao carregar operacao:', error);
        }
      }

      if (isDepot && !tipoDeCargaOption && data?.TipoDeCarga) {
        try {
          const tiposResponse = await getTiposCarga({
            CodigoIntegracao: data.TipoDeCarga,
            quantidadePorPagina: 1
          });
          const registros = Array.isArray(tiposResponse)
            ? tiposResponse
            : tiposResponse?.registros || tiposResponse?.data || [];
          const tipo = registros?.[0];
          if (tipo) {
            const value = tipo.CodigoIntegracao ?? tipo.codigoIntegracao ?? tipo.id ?? tipo.Id ?? data.TipoDeCarga;
            const label = tipo.Descricao ?? tipo.descricao ?? String(value || data.TipoDeCarga);
            tipoDeCargaOption = { value, label };
          }
        } catch (error) {
          console.error('Erro ao carregar tipo de carga:', error);
        }
        if (!tipoDeCargaOption) {
          tipoDeCargaOption = { value: data.TipoDeCarga, label: data.TipoDeCarga };
        }
      }

      const tomadorRaw =
        data?.Tomador ||
        data?.TomadorCpfCnpj ||
        data?.TomadorCnpj ||
        data?.tomadorCpfCnpj ||
        data?.tomadorCnpj ||
        tomadorOption?.cnpj ||
        tomadorOption?.label ||
        null;
      const tomadorId = data?.TomadorId ?? null;
      const tomadorCnpj = tomadorOption?.cnpj || extractDigits(tomadorRaw);

      if (isDepot && !tomadorOption && tomadorCnpj) {
        try {
          const clientesResponse = await getClientes({
            Cnpj: tomadorCnpj,
            quantidadePorPagina: 1
          });
          const registros = Array.isArray(clientesResponse)
            ? clientesResponse
            : clientesResponse?.registros || clientesResponse?.data || [];
          const cliente = registros?.[0];
          if (cliente) {
            const nome = cliente.Nome ?? cliente.nome ?? '';
            const cnpj = cliente.CNPJ ?? cliente.Cnpj ?? cliente.cnpj ?? '';
            const id = cliente.Id ?? cliente.id ?? null;
            const label = nome && cnpj ? `${nome} - ${cnpj}` : nome || cnpj || String(cnpj || tomadorRaw);
            tomadorOption = { value: id ?? tomadorId ?? null, label, cnpj };
          }
        } catch (error) {
          console.error('Erro ao carregar tomador:', error);
        }
      }

      if (isDepot && !tomadorOption && tomadorRaw && tomadorId) {
        tomadorOption = {
          value: tomadorId,
          label: tomadorRaw,
          cnpj: tomadorCnpj
        };
      } else if (isDepot && !tomadorOption && tomadorRaw) {
        tomadorOption = { value: tomadorId ?? tomadorRaw, label: tomadorRaw, cnpj: tomadorCnpj };
      }

          const tipoTomadorCodigo =
            data.CodigoTipoTomador ?? data.codigoTipoTomador ?? data.TipoTomador ?? data.tipoTomador ?? null;
          const tipoTomadorLabel =
            data.NomeTipoTomador ?? data.nomeTipoTomador ?? getOptionLabel(tipoTomadorOptions, tipoTomadorCodigo);
          const requisitanteCodigo =
            data.CodigoTipoRequisitante ?? data.codigoTipoRequisitante ?? data.Requisitante ?? data.requisitante ?? null;
          const requisitanteLabel =
            data.NomeTipoRequisitante ?? data.nomeTipoRequisitante ?? getOptionLabel(requisitanteOptions, requisitanteCodigo);

      const recebedorId = data.RecebedorId ?? data.recebedorId ?? null;
      let recebedorNome = data.Recebedor ?? data.recebedor ?? recebedorOption?.label ?? '';

      if (isDepot && recebedorId && !recebedorNome) {
        try {
          const cliente = await getCliente(recebedorId);
          recebedorNome = cliente?.Nome ?? cliente?.nome ?? '';
        } catch (error) {
          // fallback permanece com ID quando não for possível resolver nome
        }
      }

      setInitialData({
            // originais
            numeroDoPedido: data.NumeroDoPedido || '',
            localPalletizacao: data.LocalPalletizacao || '',
            dataAgendamento: data.DataAgendamento ? new Date(data.DataAgendamento) : null,

            // novas colunas (sempre opcional)
            numeroDePallets: data.NumeroDePallets ?? null,
            pesoLiquido: data.PesoLiquido ?? null,
            tipoDeRemetente: data.TipoDeRemetente || '',
            numeroPedidoEmbarcador: data.NumeroPedidoEmbarcador || '',
            dataColeta: parseBrDateTime(data.DataColeta),
            remetente: data.Remetente || remetenteOption?.label || '',
            destinatario: data.Destinatario || destinatarioOption?.label || '',
            recebedor:
              recebedorId
                ? { value: recebedorId, label: recebedorNome || String(recebedorId) }
                : recebedorNome || '',
            origem: data.Origem || '',
            destino: data.Destino || '',
            tipoDeCarga: isDepot && data.TipoDeCarga
              ? tipoDeCargaOption || { value: data.TipoDeCarga, label: data.TipoDeCarga }
              : data.TipoDeCarga || '',
            tipoDeOperacao: isDepot && data.TipoDeOperacao
              ? { value: data.TipoDeOperacao, label: nomeOperacao || data.TipoDeOperacao }
              : data.TipoDeOperacao || '',
            rota: rotaOption || data.RotaCodigo || data.Rota || '',
            empresaFilial: data.EmpresaFilial || '',
            modeloVeicularSolicitado: isDepot && (data.ModeloVeicularSolicitadoId || data.ModeloVeicularSolicitado)
              ? {
                value: data.ModeloVeicularSolicitadoId || data.ModeloVeicularSolicitado,
                label:
                  data.ModeloVeicularSolicitado ||
                  data.ModeloVeicular?.Placa ||
                  data.ModeloVeicular?.Descricao ||
                  data.ModeloVeicular?.NomeTipoModeloVeicular ||
                  data.ModeloVeicularSolicitadoId
              }
              : data.ModeloVeicularSolicitado || '',
            dataPrevisaoSaida: data.DataPrevisaoSaida ? new Date(data.DataPrevisaoSaida) : null,
            dataPrevisaoEntregaRemetente: data.DataPrevisaoEntregaRemetente ? new Date(data.DataPrevisaoEntregaRemetente) : null,
            dataInicialViagemExecutada: data.DataInicialViagemExecutada ? new Date(data.DataInicialViagemExecutada) : null,
            dataFinalViagemExecutada: data.DataFinalViagemExecutada ? new Date(data.DataFinalViagemExecutada) : null,
            dataInicialViagemFaturada: data.DataInicialViagemFaturada ? new Date(data.DataInicialViagemFaturada) : null,
            dataFinalViagemFaturada: data.DataFinalViagemFaturada ? new Date(data.DataFinalViagemFaturada) : null,
            tomador: isDepot && (tomadorRaw || tomadorOption)
              ? tomadorOption || { value: tomadorId ?? tomadorRaw, label: tomadorRaw, cnpj: tomadorCnpj }
              : tomadorRaw || '',
            tipoTomador: tipoTomadorCodigo !== '' && tipoTomadorCodigo !== null && tipoTomadorCodigo !== undefined
              ? { value: String(tipoTomadorCodigo), label: tipoTomadorLabel }
              : '',
            requisitante: requisitanteCodigo !== '' && requisitanteCodigo !== null && requisitanteCodigo !== undefined
              ? { value: String(requisitanteCodigo), label: requisitanteLabel }
              : '',
            pesoDoPedido: data.PesoDoPedido ?? null,
            quantidadeDeEntrega: data.QuantidadeDeEntrega ?? null,
            dataAgendamentoPedido: data.DataAgendamentoPedido ? new Date(data.DataAgendamentoPedido) : null,
            condicaoDoPedido: data.CondicaoDoPedido || '',
            observacaoPedido: data.ObservacaoPedido || '',
            duimp: data.DUIMP ?? data.Duimp ?? '',
            CubagemPedido: data.CubagemPedido || '',
            valorTotalMercadoria: data.ValorTotalMercadoria ?? null,
            valorFrete: data.ValorFrete ?? null,
          });
        } catch (error) {
          console.error('Erro ao carregar pedido:', error);
        } finally {
          setLoadingData(false);
        }
      };
      loadData();
    }
  }, [isReadOnly, isEditMode, id, isDepot]);

  const onSubmit = useCallback(async (values) => {
    try {
      setLoading(true);
      const payload = mapFormToCreateApi(values, depotId, { formatDataColetaBr: isDepot });

      if (isEditMode) {
        await updatePedido(id, payload);
        notification.success('Pedido atualizado com sucesso!');
      } else {
        await createPedido(payload);
        notification.success('Pedido criado com sucesso!');
      }
      history.goBack();
    } catch (err) {
      // tratado por interceptor global
    } finally {
      setLoading(false);
    }
  }, [history, id, isEditMode, depotId]);

  const defaultValues = useMemo(() => ({
    // originais
    numeroDoPedido: generateNumeroPedido(),
    localPalletizacao: '',
    dataAgendamento: null,

    // novos (todos opcionais)
    numeroDePallets: null,
    pesoLiquido: null,
    tipoDeRemetente: '',
    numeroPedidoEmbarcador: '',
    dataColeta: null,
    remetente: '',
    destinatario: '',
    recebedor: '',
    origem: '',
    destino: '',
    tipoDeCarga: '',
    tipoDeOperacao: '',
    rota: '',
    empresaFilial: '',
    modeloVeicularSolicitado: '',
    dataPrevisaoSaida: null,
    dataPrevisaoEntregaRemetente: null,
    dataInicialViagemExecutada: null,
    dataFinalViagemExecutada: null,
    dataInicialViagemFaturada: null,
    dataFinalViagemFaturada: null,
    tomador: '',
    tipoTomador: '',
    requisitante: '',
    pesoDoPedido: null,
    quantidadeDeEntrega: null,
    dataAgendamentoPedido: null,
    condicaoDoPedido: '',
    observacaoPedido: '',
    duimp: '',
    CubagemPedido: '',
    valorTotalMercadoria: null,
    valorFrete: null,
  }), []);

  const initialValues = initialData || defaultValues;

  const handleSearchTiposCarga = useCallback(async (searchTerm) => {
    try {
      const tipos = await getTiposCarga({ Descricao: searchTerm });
      const registros = Array.isArray(tipos)
        ? tipos
        : tipos?.registros || tipos?.data || [];
      return registros.map((tipo) => {
        const value = tipo.CodigoIntegracao ?? tipo.codigoIntegracao ?? tipo.id ?? tipo.Id ?? '';
        const label = tipo.Descricao ?? tipo.descricao ?? String(value || '');
        return { value, label };
      });
    } catch (error) {
      console.error('Erro ao buscar tipos de carga:', error);
      return [];
    }
  }, []);

  const handleSearchOperacoes = useCallback(async (searchTerm) => {
    try {
      const operacoes = await getOperacoes({ NomeOperacao: searchTerm });
      const registros = Array.isArray(operacoes)
        ? operacoes
        : operacoes?.registros || operacoes?.data || [];
      return registros.map((operacao) => ({
        value: operacao.TipoDaOperacao ?? operacao.tipoDaOperacao ?? '',
        label: operacao.NomeOperacao ?? operacao.nomeOperacao ?? ''
      }));
    } catch (error) {
      console.error('Erro ao buscar operacoes:', error);
      return [];
    }
  }, []);
  const handleSearchModelos = useCallback(async (searchTerm) => {
    try {
      const modelos = await getModelos({ search: searchTerm, limit: 30 });
      const registros = Array.isArray(modelos)
        ? modelos
        : modelos?.registros || modelos?.data || [];
      return registros.map((modelo) => ({
        value: modelo.id ?? modelo.Id ?? '',
        label:
          modelo.descricao ??
          modelo.Descricao ??
          modelo.nomeTipoModeloVeicular ??
          modelo.NomeTipoModeloVeicular ??
          String(modelo.id ?? modelo.Id ?? '')
      }));
    } catch (error) {
      console.error('Erro ao buscar modelos veiculares:', error);
      return [];
    }
  }, []);
  const handleSearchTomador = useCallback(async (searchTerm) => {
    try {
      const clientes = await getClientes({ Cnpj: searchTerm, quantidadePorPagina: 30 });
      const registros = Array.isArray(clientes)
        ? clientes
        : clientes?.registros || clientes?.data || [];
      return registros
        .map((cliente) => {
          const nome = cliente.Nome ?? cliente.nome ?? '';
          const cnpj = cliente.CNPJ ?? cliente.Cnpj ?? cliente.cnpj ?? '';
          const id = cliente.Id ?? cliente.id ?? '';
          const label = nome && cnpj ? `${nome} - ${cnpj}` : nome || cnpj || String(id || '');
          return { value: id, label, cnpj };
        })
        .filter((item) => item.value || item.label);
    } catch (error) {
      console.error('Erro ao buscar tomadores:', error);
      return [];
    }
  }, []);
  const baseStructure = useMemo(
    () => (isDepot ? getFormStructure({ isDepot: true }) : formStructure),
    [isDepot]
  );
  const baseSections = useMemo(
    () => (isDepot ? getFormSections(baseStructure, initialValues, { isDepot: true }) : formSections(initialValues)),
    [isDepot, baseStructure, initialValues]
  );
  const { structure: formStructureWithSearch, sections: formSectionsWithSearch } = useMemo(() => {
    if (!shouldHideObservacaoComplemento) {
      return { structure: baseStructure, sections: baseSections };
    }

    const rowIndexMap = new Map();
    let newIndex = 0;

    const structure = baseStructure
      .map((row, index) => {
        const filteredRow = row.filter((field) => field.name !== 'observacaoComplemento');
        if (filteredRow.length > 0) {
          rowIndexMap.set(index, newIndex);
          newIndex += 1;
          return filteredRow;
        }
        return null;
      })
      .filter(Boolean);

    const sections = baseSections
      .map((section) => ({
        ...section,
        rows: section.rows
          .map((rowIndex) => rowIndexMap.get(rowIndex))
          .filter((rowIndex) => rowIndex !== undefined)
      }))
      .filter((section) => section.rows.length > 0);

    return { structure, sections };
  }, [shouldHideObservacaoComplemento, baseStructure, baseSections]);
  const formStructureWithMapButton = useMemo(() => (
    formStructureWithSearch.map((row) => row.map((field) => {
      if (!field || field.name !== 'rota') return field;
      return {
        ...field,
        extraButtons: [
          {
            id: 'mapa-rota',
            title: 'Ver mapa da rota',
            icon: 'map-marker-path',
            disabled: isReadOnly,
            onClick: handleOpenRotaMap
          }
        ]
      };
    }))
  ), [formStructureWithSearch, handleOpenRotaMap, isReadOnly]);
  const modalComponents = useMemo(
    () => ({
      remetente: ClienteSelectionModal,
      destinatario: ClienteSelectionModal,
      recebedor: ClienteSelectionModal,
      rota: RotaSelectionModal
    }),
    []
  );

  if ((isReadOnly || isEditMode) && loadingData) {
    return (
      <Card>
        <CardBody>
          <div style={{ textAlign: 'center', padding: '50px' }}>
            Carregando dados do pedido...
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <Form onSubmit={onSubmit} initialValues={initialValues} validate={validateForm}>
      {({ handleSubmit, values }) => (
        <Card>
          <CardBody>
            <FormContainer onSubmit={handleSubmit}>
                <DynamicForm
                  structure={formStructureWithMapButton}
                  sections={formSectionsWithSearch}
                  isViewMode={isReadOnly}
                  isEditing={isEditMode}
                  searchFunctions={{
                    tipoDeCarga: handleSearchTiposCarga,
                    tipoDeOperacao: handleSearchOperacoes,
                    modeloVeicularSolicitado: handleSearchModelos,
                    tomador: handleSearchTomador
                  }}
                  modalComponents={modalComponents}
                />
                <RotaMapModal
                  show={showRotaMapModal}
                  onHide={() => setShowRotaMapModal(false)}
                  rota={rotaMapData}
                />

              {!isReadOnly && (
                <FormButtonToolbar className="mt-5">
                  <ButtonWithLoading variant="primary" type="submit" loading={loading ? 'true' : undefined}>
                    {isEditMode ? 'Atualizar' : 'Salvar'}
                  </ButtonWithLoading>
                  <Button variant="secondary" type="button" onClick={() => history.goBack()}>
                    Cancelar
                  </Button>
                </FormButtonToolbar>
              )}
            </FormContainer>
          </CardBody>
        </Card>
      )}
    </Form>
  );
  
};

export default Forms;





